import { useEffect, useState } from "react";
import { CalendarClock, Loader2, Send, Video } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

interface Props {
  /** The current authenticated user (must be a vet). */
  userId: string;
  /** The active conversation id to send the invite into. */
  conversationId: string;
  /** Optional compact mode for the popped chat window. */
  compact?: boolean;
}

interface VetPractice {
  id: string;
  name: string | null;
}

/**
 * Renders a popover button (vets only) that lets a vet drop a one-click invite
 * into the chat for the pet owner to either book an appointment or start a
 * GetVideoVet session with their practice. Self-detects if the current user is
 * a practice owner - renders nothing otherwise so it's safe to drop into any
 * chat composer.
 */
export const InviteToBookButton = ({ userId, conversationId, compact }: Props) => {
  const [practice, setPractice] = useState<VetPractice | null>(null);
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState<"book" | "video" | null>(null);
  const [blocked, setBlocked] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("vet_practices")
        .select("id, name")
        .eq("user_id", userId)
        .limit(1)
        .maybeSingle();
      if (!cancelled) setPractice((data as VetPractice) || null);
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  // Determine if the other chat participant is blocked by this practice
  useEffect(() => {
    if (!practice) {
      setBlocked(false);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data: parts } = await supabase
        .from("conversation_participants")
        .select("user_id")
        .eq("conversation_id", conversationId);
      const otherIds = (parts || []).map((p) => p.user_id).filter((id) => id !== userId);
      if (otherIds.length === 0) {
        if (!cancelled) setBlocked(false);
        return;
      }
      const { data: blocks } = await supabase
        .from("vet_blocked_owners")
        .select("owner_user_id")
        .eq("practice_id", practice.id)
        .in("owner_user_id", otherIds);
      if (!cancelled) setBlocked((blocks || []).length > 0);
    })();
    return () => {
      cancelled = true;
    };
  }, [practice, conversationId, userId]);

  if (!practice) return null;

  const origin =
    typeof window !== "undefined" ? window.location.origin : "https://getpetvet.co.uk";
  const practiceLabel = practice.name || "our practice";
  const bookUrl = `${origin}/book-appointment?vetId=${encodeURIComponent(practice.id)}`;
  const videoUrl = `${origin}/getvideovet`;

  const sendInvite = async (kind: "book" | "video") => {
    if (blocked) {
      toast.error("You've blocked this customer", {
        description: "Unblock them from your Customers list to send invites.",
      });
      return;
    }
    setSending(kind);
    const content =
      kind === "book"
        ? `📅 You're invited to book an appointment with ${practiceLabel}.\n\nTap to book a time that suits you: ${bookUrl}`
        : `📹 You're invited to start a GetVideoVet session with ${practiceLabel}.\n\nTalk to a vet by secure video - tap to begin: ${videoUrl}`;
    const { error } = await supabase.from("messages").insert({
      conversation_id: conversationId,
      sender_id: userId,
      content,
    });
    setSending(null);
    if (error) {
      toast.error("Couldn't send invite");
      return;
    }
    toast.success(kind === "book" ? "Booking invite sent" : "Video call invite sent");
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          title={blocked ? "You've blocked this customer" : "Invite to book"}
          aria-label="Invite to book an appointment or video call"
          disabled={blocked}
          className={cn(compact && "h-9 w-9")}
        >
          <Send className={cn("rotate-[-15deg]", compact ? "w-3.5 h-3.5" : "w-4 h-4")} />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-2">
        <p className="text-xs font-semibold text-foreground px-2 pt-1 pb-2">
          Invite to book
        </p>
        <button
          type="button"
          onClick={() => sendInvite("book")}
          disabled={sending !== null}
          className="w-full flex items-start gap-2.5 px-2 py-2 rounded-md hover:bg-muted text-left transition disabled:opacity-60"
        >
          <span className="mt-0.5 w-7 h-7 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
            {sending === "book" ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <CalendarClock className="w-3.5 h-3.5" />
            )}
          </span>
          <span className="flex-1 min-w-0">
            <span className="block text-sm font-medium">Book an appointment</span>
            <span className="block text-[11px] text-muted-foreground leading-tight">
              Send a one-click link to schedule a visit.
            </span>
          </span>
        </button>
        <button
          type="button"
          onClick={() => sendInvite("video")}
          disabled={sending !== null}
          className="w-full flex items-start gap-2.5 px-2 py-2 rounded-md hover:bg-muted text-left transition disabled:opacity-60"
        >
          <span className="mt-0.5 w-7 h-7 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
            {sending === "video" ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Video className="w-3.5 h-3.5" />
            )}
          </span>
          <span className="flex-1 min-w-0">
            <span className="block text-sm font-medium">Start GetVideoVet</span>
            <span className="block text-[11px] text-muted-foreground leading-tight">
              Invite the owner to a secure video call.
            </span>
          </span>
        </button>
      </PopoverContent>
    </Popover>
  );
};

export default InviteToBookButton;
